import React, { useRef, useEffect } from "react";
import './CaseStudyAi.css';
import { motion } from 'framer-motion';
import ecosystem from '../Assets/images/casestudy/ecocaseuserflow.png'; // placeholder image
import visualguide from '../Assets/images/casestudy/ecocasevisualguide.png'; // placeholder image
import gamification from '../Assets/images/casestudy/ecocaseoverview.png'; // placeholder image
import security from '../Assets/images/casestudy/ecocasesecurity.png'; // placeholder image
import prototypeVideo from '../Assets/Videos/casestudy/ecoscanprototype.mp4'; // new prototype video

export default function CaseStudyEco() {
  const prototypeRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && prototypeRef.current) {
          prototypeRef.current.play();
        }
      },
      { threshold: 0.5 }
    );

    if (prototypeRef.current) {
      observer.observe(prototypeRef.current);
    }

    return () => {
      if (prototypeRef.current) {
        observer.unobserve(prototypeRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* === Video Hero Section === */}
      <section className="caseStudyVideoHeader">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="caseStudyBgVideo"
        >
          <source src={require('../Assets/Videos/casestudy/ecoscanhero.mp4')} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <div className="caseStudyOverlay">
          <h1 className="caseStudyTitle">
            <span>EcoScan</span>
          </h1>
          <p className="caseStudyIntro">
            A mobile app that encourages recycling by scanning barcodes, giving real-world rewards, and helping users sort smarter.
          </p>
        </div>
      </section>

      {/* === Main Case Study Content Section === */}
      <section className="caseStudyContainer">   
      <section className="caseStudyContentWrapper">
        <motion.div
          className="caseStudyBlock"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <ul className="caseStudyList">
            <li>Created a full-featured recycling app prototype with real incentive structures</li>
            <li>Developed a complete design system and UI guide for handoff</li>
            <li>Implemented strict security compliance measures in the user experience with minimal accessibility impact.</li>
          </ul>
        </motion.div>

    <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
      <div className="caseStudyBlock">
        <h2 className="caseStudySubheading">🔍 Problem & Opportunity</h2>
        <p className="caseStudyText">
          Many users believe they're recycling correctly, yet mis-sorting remains a major issue. Me and the team learned from our visit to Renova Sävenäs Waste-to-energy plant that this misplacement leads to major impacts on the reuse pipeline through infiltration of hazardous materials and burning of material that would otherwise be recycled. With EcoScan, I sought to tackle both user uncertainty and lack of motivation, offering real rewards for proper sorting and circumnavigation of the fact that users do not know what they dont know about recycling.
        </p>
      </div>
    </motion.section>

    <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
      <div className="caseStudyContent">
        <div className="caseStudyTextBlock">
          <h2 className="caseStudySubheading">🎯 App Goals</h2>
          <p className="caseStudyText">
            The goal was to make motivate correct recycling practices through smart UX-writing paired with a proper incentive system. The app uses barcode scanning to identify product types and guides the user on proper disposal. Rewards are given for completing actions correctly, encouraging habit-building around sustainable behavior.
          </p>
        </div>
        <img src={ecosystem} alt="EcoScan app process overview" className="caseStudyImage" />
      </div>
    </motion.section>

    <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
      <div className="caseStudyContent">
        <div className="caseStudyTextBlock">
          <h2 className="caseStudySubheading">🛠️ Key Design Decisions</h2>
          <p className="caseStudyText">
            Due to time constraints, we leaned on best practices for mobile UX: accessible navigation, contrast, and clear feedback loops. Audio and visual cues make scanning intuitive, even in low-light or noisy environments.
          </p>
        </div>
        <img src={visualguide} alt="EcoScan visual and design system" className="caseStudyImage" />
      </div>
    </motion.section>

    <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
      <div className="caseStudyContent">
        <div className="caseStudyTextBlock">
          <h2 className="caseStudySubheading">🛡️ Security Considerations</h2>
          <p className="caseStudyText">
            Since barcodes don't identify individual items, we implemented a smart constraint system: users gain rewards from one item per product type per session, up to a maximum limit. Users are limited to one session per day. Identity is verified using BankID and identification of users through payment processors to ensure reward eligibility and reduce fraud risk. Geo-tracking is implemented, where users only gain points in designated recycling stations. All these data points can then be correlated to easily identify abuse.
          </p>
        </div>
        <img src={security} alt="Security integrations in EcoScan" className="caseStudyImage" />
      </div>
    </motion.section>

    <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
      <div className="caseStudyContent">
        <div className="caseStudyTextBlock">
          <h2 className="caseStudySubheading">🎮 Gamification & Rewards</h2>
          <p className="caseStudyText">
            Users are rewarded with points for successful scans, redeemable for motivating incentives. We used sounds, animations, and visual feedback to provide a tactile experience where a minimal amount of taps are needed to operate the app. This greatly helped the app be more accessable in the recycling process where hand space is limited and interaction with the device after handling the materials is undesirable.
          </p>
        </div>
        <img src={gamification} alt="Gamification features in EcoScan app" className="caseStudyImage" />
      </div>
    </motion.section>

    <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
      <div className="caseStudyBlock">
        <h2 className="caseStudySubheading">📱 The Application</h2>
        <ul className="caseStudyList">
          <li>Barcode scanning interface with live camera feed</li>
          <li>Audio-visual confirmation and scan feedback</li>
          <li>BankID integration for identity verification</li>
          <li>Session-based tracking & product type limits</li>
          <li>Points system tied to verified actions</li>
          <li>Accessible design system and component library</li>
        </ul>
      </div>
    </motion.section>

    <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
      <div className="caseStudyBlock">
        <h2 className="caseStudySubheading">🎥 Prototype Demo</h2>
        <video
          ref={prototypeRef}
          muted
          loop
          playsInline
          className="caseStudyImage"
          style={{ width: '100%', borderRadius: '0', maxWidth: '100%' }}
        >
          <source src={prototypeVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    </motion.section>

    </section>
    </section>
    <footer className="caseStudyFooter">
      <a href="/" className="caseStudyBackLink">← Back to Home</a>
    </footer>
  </>
);}
